import React, { ReactNode, useEffect, useState } from 'react'
import Head from 'next/head'
import Header from './layout/Header'
import Footer from './layout/Footer'
import SkipLinks from './accessibility/SkipLinks'
import { useTheme } from '@/contexts/ThemeContext'
import { getSiteSettings } from '@/lib/api'
import { SiteSettings } from '@/types'
import { cn } from '@/lib/utils'
import dynamic from 'next/dynamic'

// Lazy load performance monitor for development
const PerformanceMonitor = dynamic(() => import('./performance/PerformanceMonitor'), { ssr: false })

// Import accessibility testing in development
if (process.env.NODE_ENV === 'development') {
  import('@/lib/accessibilityTesting')
}

type LayoutProps = {
  children: ReactNode
  title?: string
  description?: string
  className?: string
  hideHeader?: boolean
  hideFooter?: boolean
  fullWidth?: boolean
  ogImage?: string
  noIndex?: boolean
}

const Layout = ({
  children,
  title,
  description,
  className = '',
  hideHeader = false,
  hideFooter = false,
  fullWidth = false,
  ogImage,
  noIndex = false,
}: LayoutProps) => {
  const { theme } = useTheme()
  const [siteSettings, setSiteSettings] = useState<Partial<SiteSettings> | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSiteSettings()
        setSiteSettings(settings)
        setIsLoading(false)
      } catch (error) {
        console.error('Failed to fetch site settings:', error)
        setIsLoading(false)
      }
    }

    fetchSettings()
  }, [])

  // Use site settings for title and description if not provided as props
  const pageTitle = title || siteSettings?.siteTitle || 'Blockchain Developer Portfolio'
  const pageDescription =
    description ||
    siteSettings?.description ||
    'A portfolio showcasing blockchain development projects and skills'

  return (
    <>
      {/* <SkipLinks /> */}
      <Head>
        <title>{pageTitle}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta name="description" content={pageDescription} />

        {/* Favicon */}
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="manifest" href="/site.webmanifest" />

        {/* Fonts */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@500;600;700&family=Fira+Code:wght@400;500&display=swap"
          rel="stylesheet"
        />

        {/* Theme color meta tag for mobile browsers */}
        <meta name="theme-color" content={theme === 'dark' ? '#121212' : '#ffffff'} />

        {/* Open Graph / Social Media */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta
          property="og:site_name"
          content={siteSettings?.siteTitle || 'Blockchain Developer Portfolio'}
        />
        {ogImage && <meta property="og:image" content={ogImage} />}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}

        {/* No index if specified */}
        {noIndex && <meta name="robots" content="noindex" />}
      </Head>

      <div
        className={cn(
          'flex flex-col min-h-screen transition-colors duration-200',
          theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900',
          className
        )}
      >
        {!hideHeader && <Header />}

        <main
          id="main-content"
          className={cn('flex-grow', !fullWidth && 'container mx-auto px-4 sm:px-6 lg:px-8 py-8')}
          tabIndex={-1}
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            children
          )}
        </main>

        {!hideFooter && <Footer />}

        {/* Performance monitor for development */}
        <PerformanceMonitor />
      </div>
    </>
  )
}

export default Layout
